// Commandes, livraisons et factures reçues (M15, lot L4-C, D-142) : commandes sur contrat, livraisons
// réceptionnées par le service (conformes ou avec réserves), factures enregistrées dans le module
// finances puis rattachées à une liquidation par le financier (chaîne de la dépense).
import type { CommandeAchat } from '@cc/contrats';
import { Bouton, EtatVide, Lien, PuceEtat, Tableau, type ColonneTableau } from '@cc/ui';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { useCommandes, useContrats } from './api';
import { FormulaireCommande, FormulaireFacture, FormulaireLivraison } from './FormulairesContrats';
import { francs, libelleCommande, TON_COMMANDE } from './logique';
import { NavigationAchats } from './NavigationAchats';
import styles from './achats.module.css';

type Dialogue = { type: 'commande' } | { type: 'livraison' | 'facture'; commande: CommandeAchat } | null;

export function PageCommandes() {
  const { contexte } = useSession();
  const commandes = useCommandes();
  const contrats = useContrats();
  const [d, setD] = useState<Dialogue>(null);
  const liste = commandes.data?.elements ?? [];
  const factures = liste.flatMap((c) => c.factures.map((f) => ({ ...f, commande: c })));
  const colonnes: ColonneTableau<CommandeAchat>[] = [
    { id: 'numero', entete: 'Commande', enTeteLigne: true, masquable: false, cellule: (c) => <span className="cc-mono">{c.numero}</span>, valeurTri: (c) => c.numero },
    { id: 'objet', entete: 'Objet', cellule: (c) => c.objet },
    { id: 'fournisseur', entete: 'Fournisseur', cellule: (c) => `${c.fournisseur.nom}${c.contrat ? ` (contrat ${c.contrat.numero})` : ''}` },
    { id: 'montant', entete: 'Montant', numerique: true, cellule: (c) => francs(c.montant, c.fictif), valeurTri: (c) => c.montant },
    { id: 'dates', entete: 'Commandée / prévue', cellule: (c) => `${c.date_commande}${c.livraison_prevue ? ` / ${c.livraison_prevue}` : ''}` },
    { id: 'livraisons', entete: 'Livraisons', cellule: (c) => (c.livraisons.length === 0 ? 'Aucune' : c.livraisons.map((l) => `${l.date_livraison} ${l.conforme ? 'conforme' : `avec réserves : ${l.reserves ?? ''}`}`).join(' ; ')) },
    { id: 'etat', entete: 'État', cellule: (c) => <PuceEtat ton={TON_COMMANDE[c.etat]} libelle={libelleCommande(c.etat)} /> },
    {
      id: 'actes', entete: 'Actes', masquable: false, cellule: (c) => (
        <div className={styles.actesLigne}>
          {['emise', 'livree_partiellement'].includes(c.etat) && peut(contexte, 'achat', 'creer') ? <Bouton variante="secondaire" onClick={() => setD({ type: 'livraison', commande: c })}>Réceptionner</Bouton> : null}
          {c.etat !== 'annulee' && peut(contexte, 'depense', 'modifier') ? <Bouton variante="secondaire" onClick={() => setD({ type: 'facture', commande: c })}>Facture reçue</Bouton> : null}
        </div>
      ),
    },
  ];
  return (
    <>
      <EnTetePage
        etiquette="Marchés publics et fournisseurs"
        titre="Commandes, livraisons et factures"
        description="Exécution des contrats : commandes, réception des livraisons par le service, factures reçues rattachées à la chaîne de la dépense."
        actions={peut(contexte, 'achat', 'modifier') ? <Bouton variante="principal" icone={Plus} onClick={() => setD({ type: 'commande' })}>Émettre une commande</Bouton> : null}
      />
      <NavigationAchats actif="commandes" />
      <EtatRequete chargement={commandes.isPending} erreur={commandes.error} onReessayer={() => void commandes.refetch()} lot="L4-C" fonction="Les commandes">
        <section className={styles.bloc} aria-labelledby="titre-commandes">
          <h2 id="titre-commandes">Commandes</h2>
          {liste.length === 0 ? <EtatVide titre="Aucune commande"><p>Aucune commande n&apos;est émise.</p></EtatVide> : <Tableau legende="Commandes" colonnes={colonnes} lignes={liste} cleLigne={(c) => c.id} cleColonnes="achats-commandes" />}
        </section>
        <section className={styles.bloc} aria-labelledby="titre-factures">
          <h2 id="titre-factures">Factures reçues</h2>
          <p className={styles.detail}>La facture appartient au module finances : le financier la rattache à une liquidation dans la <Lien href="/budget/depense">chaîne de la dépense</Lien>.</p>
          {factures.length === 0 ? <p className={styles.detail}>Aucune facture reçue sur les commandes.</p> : (
            <ul className={styles.liste}>
              {factures.map((f) => (
                <li key={f.id}>
                  <span className="cc-mono">{f.numero}</span> : facture {f.numero_fournisseur} de {f.commande.fournisseur.nom}, {francs(f.montant, f.commande.fictif)}, commande {f.commande.numero} ;{' '}
                  {f.liquidation ? `liquidée (${f.liquidation.numero})` : f.etat === 'annulee' ? 'annulée' : 'à liquider'}
                </li>
              ))}
            </ul>
          )}
        </section>
      </EtatRequete>
      {d?.type === 'commande' ? <FormulaireCommande contrats={contrats.data?.elements ?? []} onFermer={() => setD(null)} /> : null}
      {d?.type === 'livraison' ? <FormulaireLivraison commande={d.commande} onFermer={() => setD(null)} /> : null}
      {d?.type === 'facture' ? <FormulaireFacture commande={d.commande} onFermer={() => setD(null)} /> : null}
    </>
  );
}
